import { SPLIT_SYSTEM_DUAL_SUBTYPES } from '../config/options';
import type { HvacSystemValues } from './HvacSystemFields';

const INDOOR_OUTDOOR_RESET: Partial<HvacSystemValues> = {
  indoorManufacturer: '',
  outdoorManufacturer: '',
  indoorAge: '',
  indoorAgeCondition: '',
  outdoorAge: '',
  outdoorAgeCondition: '',
};

/**
 * Fields to blank when the system type changes (the new type itself is not included).
 * Split-only fields are cleared unless it is still a split system; boiler-only answers unless it is a boiler,
 * so stale answers for questions that are no longer shown are never submitted.
 */
export const resetFieldsForSystemType = (nextType: string): Partial<HvacSystemValues> => ({
  ...(nextType !== 'split_system' && { splitSystemSubtype: '', ...INDOOR_OUTDOOR_RESET }),
  ...(nextType !== 'boiler' && {
    boilerAcPresent: '',
    boilerExpansionTankCondition: '',
    boilerHeatExchangerCondition: '',
    boilerPressureReliefCondition: '',
    boilerAutoFillCondition: '',
  }),
});

/**
 * Fields to blank when the split sub-type changes (the new sub-type itself is not included).
 * Dual sub-types track an indoor and an outdoor unit; the others track one manufacturer/age.
 * Crossing between the two kinds clears the fields the new kind does not show.
 */
export const resetFieldsForSubtype = (
  values: Pick<HvacSystemValues, 'splitSystemSubtype'>,
  nextSubtype: string,
): Partial<HvacSystemValues> => {
  const wasDual = SPLIT_SYSTEM_DUAL_SUBTYPES.has(values.splitSystemSubtype);
  const isNowDual = SPLIT_SYSTEM_DUAL_SUBTYPES.has(nextSubtype);
  return {
    ...(wasDual && !isNowDual && INDOOR_OUTDOOR_RESET),
    ...(!wasDual && isNowDual && { manufacturer: '', age: '', ageCondition: '' }),
  };
};
